/* ═══════════════════════════════════════════════════════════════
   A tool panel, dropped down from its top-bar button.

   Contract §2.8: clicking a panel button drops that panel ANCHORED
   below the button — full UI, portalled — and clicking anywhere
   outside closes it. (Click-outside, deliberately, not mouse-leave:
   a palette full of drag handles and text fields that vanished when
   the pointer strayed would be unusable. Josh's note allows the flip
   later; it is one line.)

   This is the access path that makes docking optional: the panel is
   the SAME body the dock hosts, so nothing is a cut-down "menu
   version" of anything.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { PictureInPicture2, X } from 'lucide-react';

export interface PanelDropdownProps {
  title: string;
  /** The button it hangs from. */
  anchor: { left: number; bottom: number };
  /** Turn this into a free-floating panel at the same spot. */
  onFloat: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

const WIDTH = 360;
const MARGIN = 8;

export function PanelDropdown({ title, anchor, onFloat, onClose, children }: PanelDropdownProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  // Dismiss on mousedown-outside — never mouseup, never hover-leave, so a
  // text selection dragged out of the panel does not close it (DESIGN §4).
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      // A click on the button that owns this panel is a TOGGLE, handled
      // there; closing here too would reopen-then-close on the same click.
      if ((t as HTMLElement).closest?.('.dsg-panel-btn')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  const left = Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - WIDTH - MARGIN));
  const maxHeight = Math.max(220, window.innerHeight - anchor.bottom - MARGIN * 3);

  return createPortal(
    <section
      ref={ref}
      className="dsg-panel-drop"
      role="dialog"
      aria-label={title}
      style={{ left, top: anchor.bottom + 4, width: WIDTH, maxHeight }}
    >
      <header className="dsg-panel-drop-bar">
        <span className="dsg-panel-drop-title">{title}</span>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={onFloat}
          aria-label={`Keep ${title} open in a floating panel`}
          title="Keep it open — float this panel"
        >
          <PictureInPicture2 size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={onClose}
          aria-label={`Close ${title}`}
          title="Close"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </header>
      <div className="dsg-panel-drop-body">{children}</div>
    </section>,
    document.body
  );
}
